import { MOVE_SERVICE_VISIT_BOARD_CARD } from "@/modules/service-visits/store/service-visits.module";
import {
  areServiceVisitTasksTerminal,
  isServiceVisitFinished,
  serviceVisitResponseErrorMessage
} from "@/modules/service-visits/services/service-visit.mapper";

export const serviceVisitBoardDragMixin = {
  data() {
    return {
      draggedVisit: null,
      dragOverStatusId: null,
      dragOverIndex: null,
      dragPointerId: null,
      dragPreview: null,
      dragScrollFrame: null,
      dragScrollPointer: null,
      dragScrollColumnBody: null,
      isMovingVisit: false
    };
  },
  computed: {
    dragPreviewStyle() {
      if (!this.dragPreview) {
        return {};
      }

      const x = Math.round(this.dragPreview.x);
      const y = Math.round(this.dragPreview.y);

      return {
        transform: `translate3d(${x}px, ${y}px, 0)`,
        width: `${Math.round(this.dragPreview.width)}px`
      };
    }
  },
  beforeDestroy() {
    this.stopVisitPointerTracking();
  },
  methods: {
    canMoveVisit(visit) {
      return Boolean(visit.canManageBoard);
    },
    isDraggedVisit(visit) {
      return this.draggedVisit && Number(this.draggedVisit.id) === Number(visit.id);
    },
    isVisitDropZone(statusId, index) {
      return (
        this.dragOverIndex === index &&
        Number(this.dragOverStatusId) === Number(statusId)
      );
    },
    startVisitPointerDrag(visit, statusId, event) {
      const dragHandle = this.visitDragHandleFromPointerEvent(event);

      if (event.button !== 0 || !dragHandle) {
        return;
      }

      if (!this.canMoveVisit(visit) || this.isMovingVisit) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      const cardElement = dragHandle.closest(".service-visit-board-card-wrap");
      const dragElement = cardElement || dragHandle;
      const bounds = dragElement.getBoundingClientRect();

      this.draggedVisit = {
        id: visit.id,
        statusId,
        isFinished: isServiceVisitFinished(visit)
      };
      this.dragPointerId = event.pointerId;
      this.dragPreview = {
        visit,
        width: bounds.width,
        offsetX: event.clientX - bounds.left,
        offsetY: event.clientY - bounds.top,
        x: bounds.left,
        y: bounds.top
      };
      this.clearVisitDropState();
      this.dragScrollColumnBody = dragElement.closest(".board-column__body");
      this.trackVisitDragPointer(event);
      this.addVisitDragListeners();
      this.startVisitAutoScroll();
      this.updateVisitDropZoneFromPointer(event.clientX, event.clientY);
    },
    dropVisitAt(statusId, dropIndex) {
      if (
        !this.draggedVisit ||
        dropIndex === null ||
        dropIndex === undefined
      ) {
        this.finishVisitDrag();
        return;
      }

      const payload = this.moveVisitPayload(statusId, dropIndex);

      if (!payload) {
        this.finishVisitDrag();
        return;
      }

      const visit = this.visits.find(
        item => Number(item.id) === Number(this.draggedVisit.id)
      );

      if (
        !this.draggedVisit.isFinished &&
        this.isFinishedVisitStatus(statusId)
      ) {
        this.finishVisitDrag();

        if (!areServiceVisitTasksTerminal(visit)) {
          this.showFinishVisitTasksIncompleteToast();
          return;
        }

        if (typeof this.openFinishVisitModal === "function") {
          this.openFinishVisitModal(payload, visit);
        }

        return;
      }

      this.draggedVisit = null;
      this.clearVisitDropState();
      this.moveVisit(payload).catch(response => {
        this.showVisitReorderFailedToast(response);
      });
    },
    moveVisit(payload) {
      this.isMovingVisit = true;

      return this.$store
        .dispatch(MOVE_SERVICE_VISIT_BOARD_CARD, payload)
        .then(response => {
          if (this.hasActiveServiceVisitFilters && typeof this.getBoard === "function") {
            return this.getBoard().then(() => {
              this.showVisitReorderedToast();
            });
          }

          this.visits = response.data.visits || [];
          this.showVisitReorderedToast();
        })
        .catch(response => Promise.reject(response))
        .finally(() => {
          this.isMovingVisit = false;
          this.finishVisitDrag();
        });
    },
    isFinishedVisitStatus(statusId) {
      const column = (this.columns || []).find(
        item => Number(item.status.id) === Number(statusId)
      );

      return Boolean(column && column.status.slug === "finished");
    },
    moveVisitPayload(statusId, dropIndex) {
      if (
        this.draggedVisit.isFinished &&
        Number(statusId) !== Number(this.draggedVisit.statusId)
      ) {
        return null;
      }

      const visibleVisits = this.reorderedVisibleVisits(statusId, dropIndex);
      const movedIndex = visibleVisits.findIndex(
        visit => Number(visit.id) === Number(this.draggedVisit.id)
      );

      if (movedIndex < 0) {
        return null;
      }

      return {
        visitId: this.draggedVisit.id,
        statusId,
        visitAboveId: movedIndex > 0 ? visibleVisits[movedIndex - 1].id : null,
        visitBelowId:
          movedIndex < visibleVisits.length - 1 ? visibleVisits[movedIndex + 1].id : null
      };
    },
    reorderedVisibleVisits(statusId, dropIndex) {
      const isSameStatus = Number(statusId) === Number(this.draggedVisit.statusId);
      const statusVisits = this.visitsByStatus[statusId] || [];
      const originalIndex = statusVisits.findIndex(
        visit => Number(visit.id) === Number(this.draggedVisit.id)
      );

      if (isSameStatus && originalIndex < 0) {
        return [];
      }

      const draggedVisit = this.visits.find(
        visit => Number(visit.id) === Number(this.draggedVisit.id)
      );

      if (!draggedVisit) {
        return [];
      }

      const visibleVisits = statusVisits.filter(
        visit => Number(visit.id) !== Number(this.draggedVisit.id)
      );
      const adjustedDropIndex = isSameStatus && originalIndex < dropIndex
        ? dropIndex - 1
        : dropIndex;
      const boundedDropIndex = Math.max(
        0,
        Math.min(adjustedDropIndex, visibleVisits.length)
      );

      if (isSameStatus && boundedDropIndex === originalIndex) {
        return [];
      }

      visibleVisits.splice(boundedDropIndex, 0, draggedVisit);

      return visibleVisits;
    },
    finishVisitDrag() {
      this.draggedVisit = null;
      this.clearVisitDropState();
      this.stopVisitPointerTracking();
    },
    clearVisitDropState() {
      this.dragOverStatusId = null;
      this.dragOverIndex = null;
    },
    stopVisitPointerTracking() {
      this.dragPointerId = null;
      this.dragPreview = null;
      this.dragScrollColumnBody = null;
      this.dragScrollPointer = null;
      this.stopVisitAutoScroll();
      this.removeVisitDragListeners();
    },
    addVisitDragListeners() {
      this.removeVisitDragListeners();
      window.addEventListener("pointermove", this.handleWindowVisitPointerMove);
      window.addEventListener("pointerup", this.handleWindowVisitPointerUp);
      window.addEventListener("pointercancel", this.cancelVisitPointerDrag);
      window.addEventListener("keydown", this.handleVisitDragKeydown);
      window.addEventListener("wheel", this.handleWindowVisitWheel, { passive: false });
    },
    removeVisitDragListeners() {
      window.removeEventListener("pointermove", this.handleWindowVisitPointerMove);
      window.removeEventListener("pointerup", this.handleWindowVisitPointerUp);
      window.removeEventListener("pointercancel", this.cancelVisitPointerDrag);
      window.removeEventListener("keydown", this.handleVisitDragKeydown);
      window.removeEventListener("wheel", this.handleWindowVisitWheel);
    },
    handleWindowVisitPointerMove(event) {
      if (!this.draggedVisit || event.pointerId !== this.dragPointerId) {
        return;
      }

      event.preventDefault();
      this.trackVisitDragPointer(event);
      this.updateVisitPreviewPosition(event.clientX, event.clientY);
      this.updateVisitDropZoneFromPointer(event.clientX, event.clientY);
    },
    handleWindowVisitPointerUp(event) {
      if (!this.draggedVisit || event.pointerId !== this.dragPointerId) {
        return;
      }

      event.preventDefault();

      const statusId = this.dragOverStatusId;
      const dropIndex = this.dragOverIndex;

      this.stopVisitPointerTracking();
      this.dropVisitAt(statusId, dropIndex);
    },
    cancelVisitPointerDrag() {
      this.finishVisitDrag();
    },
    handleVisitDragKeydown(event) {
      if (event.key === "Escape") {
        this.finishVisitDrag();
      }
    },
    handleWindowVisitWheel(event) {
      if (!this.draggedVisit) {
        return;
      }

      event.preventDefault();

      const columnBody = event.target && event.target.closest
        ? event.target.closest(".board-column__body")
        : null;
      const activeColumnBody = columnBody || this.dragScrollColumnBody;
      const horizontalDelta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
      const verticalDelta = event.shiftKey ? 0 : event.deltaY;

      if (activeColumnBody && verticalDelta) {
        const didScrollColumn = this.scrollElementBy(activeColumnBody, 0, verticalDelta);

        if (!didScrollColumn) {
          window.scrollBy(0, verticalDelta);
        }
      }

      if (this.$refs.boardScroll && horizontalDelta) {
        this.scrollElementBy(this.$refs.boardScroll, horizontalDelta, 0);
      }
    },
    updateVisitPreviewPosition(clientX, clientY) {
      if (!this.dragPreview) {
        return;
      }

      this.dragPreview = {
        ...this.dragPreview,
        x: clientX - this.dragPreview.offsetX,
        y: clientY - this.dragPreview.offsetY
      };
    },
    updateVisitDropZoneFromPointer(clientX, clientY) {
      const columnBody = this.visitColumnBodyFromPointer(clientX, clientY);

      if (
        !columnBody ||
        !this.draggedVisit
      ) {
        this.clearVisitDropState();
        return;
      }

      this.dragScrollColumnBody = columnBody;
      const targetStatusId = Number(columnBody.dataset.statusId);

      if (
        this.draggedVisit.isFinished &&
        targetStatusId !== Number(this.draggedVisit.statusId)
      ) {
        this.clearVisitDropState();
        return;
      }

      const cardElements = Array.from(
        columnBody.querySelectorAll(".service-visit-board-card-wrap")
      );
      const dropIndex = cardElements.findIndex(cardElement => {
        const bounds = cardElement.getBoundingClientRect();

        return clientY < bounds.top + bounds.height / 2;
      });

      this.dragOverStatusId = targetStatusId;
      this.dragOverIndex = dropIndex >= 0 ? dropIndex : cardElements.length;
    },
    visitColumnBodyFromPointer(clientX, clientY) {
      const pointedElement = document.elementFromPoint(clientX, clientY);
      const pointedColumn = pointedElement && pointedElement.closest
        ? pointedElement.closest(".board-column__body")
        : null;

      if (pointedColumn) {
        return pointedColumn;
      }

      if (!this.dragScrollColumnBody) {
        return null;
      }

      const bounds = this.dragScrollColumnBody.getBoundingClientRect();
      const verticalTolerance = 120;

      if (
        clientX >= bounds.left &&
        clientX <= bounds.right &&
        clientY >= bounds.top - verticalTolerance &&
        clientY <= bounds.bottom + verticalTolerance
      ) {
        return this.dragScrollColumnBody;
      }

      return null;
    },
    trackVisitDragPointer(event) {
      this.dragScrollPointer = {
        x: event.clientX,
        y: event.clientY
      };
    },
    startVisitAutoScroll() {
      if (this.dragScrollFrame) {
        return;
      }

      const scroll = () => {
        this.scrollVisitDragContainers();
        this.dragScrollFrame = window.requestAnimationFrame(scroll);
      };

      this.dragScrollFrame = window.requestAnimationFrame(scroll);
    },
    stopVisitAutoScroll() {
      if (!this.dragScrollFrame) {
        return;
      }

      window.cancelAnimationFrame(this.dragScrollFrame);
      this.dragScrollFrame = null;
    },
    scrollVisitDragContainers() {
      if (!this.draggedVisit || !this.dragScrollPointer) {
        return;
      }

      const pointer = this.dragScrollPointer;
      let didScroll = false;

      if (this.dragScrollColumnBody) {
        didScroll = this.scrollElementVertically(this.dragScrollColumnBody, pointer.y);
      }

      if (this.$refs.boardScroll) {
        didScroll = this.scrollElementHorizontally(this.$refs.boardScroll, pointer.x) || didScroll;
      }

      didScroll = this.scrollWindowVertically(pointer.y) || didScroll;

      if (didScroll) {
        this.updateVisitDropZoneFromPointer(pointer.x, pointer.y);
      }
    },
    scrollElementVertically(element, pointerY) {
      const bounds = element.getBoundingClientRect();
      const edgeSize = 72;
      const maxSpeed = 18;
      let distance = 0;

      if (pointerY < bounds.top + edgeSize) {
        distance = -this.dragScrollSpeed(bounds.top + edgeSize - pointerY, edgeSize, maxSpeed);
      } else if (pointerY > bounds.bottom - edgeSize) {
        distance = this.dragScrollSpeed(pointerY - (bounds.bottom - edgeSize), edgeSize, maxSpeed);
      }

      if (!distance) {
        return false;
      }

      const before = element.scrollTop;
      element.scrollTop += distance;

      return element.scrollTop !== before;
    },
    scrollElementHorizontally(element, pointerX) {
      const bounds = element.getBoundingClientRect();
      const edgeSize = 96;
      const maxSpeed = 20;
      let distance = 0;

      if (pointerX < bounds.left + edgeSize) {
        distance = -this.dragScrollSpeed(bounds.left + edgeSize - pointerX, edgeSize, maxSpeed);
      } else if (pointerX > bounds.right - edgeSize) {
        distance = this.dragScrollSpeed(pointerX - (bounds.right - edgeSize), edgeSize, maxSpeed);
      }

      if (!distance) {
        return false;
      }

      const before = element.scrollLeft;
      element.scrollLeft += distance;

      return element.scrollLeft !== before;
    },
    scrollElementBy(element, left, top) {
      const beforeLeft = element.scrollLeft;
      const beforeTop = element.scrollTop;

      element.scrollLeft += left;
      element.scrollTop += top;

      return element.scrollLeft !== beforeLeft || element.scrollTop !== beforeTop;
    },
    scrollWindowVertically(pointerY) {
      const edgeSize = 88;
      const maxSpeed = 18;
      let distance = 0;

      if (pointerY < edgeSize) {
        distance = -this.dragScrollSpeed(edgeSize - pointerY, edgeSize, maxSpeed);
      } else if (pointerY > window.innerHeight - edgeSize) {
        distance = this.dragScrollSpeed(pointerY - (window.innerHeight - edgeSize), edgeSize, maxSpeed);
      }

      if (!distance) {
        return false;
      }

      const before = window.pageYOffset;
      window.scrollBy(0, distance);

      return window.pageYOffset !== before;
    },
    dragScrollSpeed(distanceIntoEdge, edgeSize, maxSpeed) {
      return Math.ceil(Math.min(distanceIntoEdge / edgeSize, 1) * maxSpeed);
    },
    visitDragHandleFromPointerEvent(event) {
      const target = event && event.target;

      return target && target.closest
        ? target.closest(".visit-card__drag-grip")
        : null;
    },
    showVisitReorderedToast() {
      if (!this.$notify) {
        return;
      }

      this.$notify({
        group: "notify",
        type: "success",
        title: `<i class='flaticon2-checkmark'></i> ${this.$t("ALERTS.SUCCESS")}`,
        text: this.$t("SERVICE_VISITS.VISIT_REORDERED")
      });
    },
    showVisitReorderFailedToast(response) {
      if (!this.$notify) {
        return;
      }

      const message = serviceVisitResponseErrorMessage(response);

      this.$notify({
        group: "notify",
        type: "error",
        title: `<i class='flaticon-close'></i> ${this.$t("ALERTS.WARNING")}`,
        text: message || this.$t("SERVICE_VISITS.VISIT_REORDER_FAILED")
      });
    },
    showFinishVisitTasksIncompleteToast() {
      if (!this.$notify) {
        return;
      }

      this.$notify({
        group: "notify",
        type: "error",
        title: `<i class='flaticon-close'></i> ${this.$t("ALERTS.WARNING")}`,
        text: this.$t("SERVICE_VISITS.TASKS_MUST_BE_TERMINAL")
      });
    }
  }
};
